import { useState, useEffect, useCallback, useRef } from 'react';
import {
  Page,
  Layout,
  Card,
  IndexTable,
  Badge,
  Banner,
  Button,
  Modal,
  TextField,
  Checkbox,
  BlockStack,
  InlineStack,
  Text,
  Divider,
  Tabs,
  Collapsible,
  Autocomplete,
  Icon,
  Tag,
  useIndexResourceState,
} from '@shopify/polaris';
import { SearchIcon } from '@shopify/polaris-icons';
import { apiGet, apiPost, apiPut, apiDelete } from '../api';

// ── Supported locales (excluding default Spanish) ──────────────

const TRANSLATION_LOCALES = [
  { code: 'en', name: 'English' },
  { code: 'pt', name: 'Português' },
  { code: 'fr', name: 'Français' },
  { code: 'it', name: 'Italiano' },
  { code: 'nl', name: 'Nederlands' },
  { code: 'de', name: 'Deutsch' },
  { code: 'pl', name: 'Polski' },
];

// ── Types ──────────────────────────────────────────────────────

interface OfferRule {
  id?: string;
  minOrderAmount: number | null;
  maxOrderAmount: number | null;
  discountPercent: number | null;
  discountFixed: number | null;
  freeShipping: boolean;
  freeProductVariantId: string | null;
  freeProductTitle: string | null;
  sortOrder: number;
}

interface Offer {
  id: string;
  name: string;
  description: string | null;
  active: boolean;
  priority: number;
  stackable: boolean;
  startsAt: string | null;
  endsAt: string | null;
  rules: OfferRule[];
  createdAt: string;
}

const EMPTY_RULE: OfferRule = {
  minOrderAmount: null,
  maxOrderAmount: null,
  discountPercent: null,
  discountFixed: null,
  freeShipping: false,
  freeProductVariantId: null,
  freeProductTitle: null,
  sortOrder: 0,
};

// ── Helpers ────────────────────────────────────────────────────

function numToStr(val: number | null): string {
  if (val === null || val === undefined) return '';
  return String(val);
}

function strToNum(s: string): number | null {
  if (!s || s.trim() === '') return null;
  const n = parseFloat(s);
  return isNaN(n) ? null : n;
}

function describeRule(r: OfferRule): string {
  const parts: string[] = [];

  // Conditions
  const conditions: string[] = [];
  if (r.minOrderAmount) conditions.push(`pedido ≥ ${r.minOrderAmount}€`);
  if (r.maxOrderAmount) conditions.push(`pedido ≤ ${r.maxOrderAmount}€`);

  // Benefits
  if (r.discountPercent) parts.push(`${r.discountPercent}% descuento`);
  if (r.discountFixed) parts.push(`${r.discountFixed}€ descuento`);
  if (r.freeShipping) parts.push('Envío gratis');
  if (r.freeProductTitle) parts.push(`Regalo: ${r.freeProductTitle}`);

  if (parts.length === 0) parts.push('Sin beneficio');

  return conditions.length > 0
    ? `Si ${conditions.join(' y ')} → ${parts.join(' + ')}`
    : parts.join(' + ');
}

// ── Component ──────────────────────────────────────────────────

// ── Product Search Component ───────────────────────────────────

function ProductSearchField({
  value,
  label,
  onSelect,
  onClear,
}: {
  value: { variantId: string; title: string } | null;
  label: string;
  onSelect: (variantId: string, productTitle: string, variantLabel: string) => void;
  onClear: () => void;
}) {
  const [inputValue, setInputValue] = useState('');
  const [options, setOptions] = useState<Array<{ value: string; label: string; productTitle: string }>>([]);
  const [loadingResults, setLoadingResults] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const searchProducts = useCallback(async (query: string) => {
    setLoadingResults(true);
    try {
      const data = await apiGet<{ products: Array<{ id: string; title: string; variants: Array<{ id: string; title: string; price: string }> }> }>(
        `/panel-api/products/search?q=${encodeURIComponent(query)}`,
      );
      const opts: Array<{ value: string; label: string; productTitle: string }> = [];
      for (const p of data.products) {
        for (const v of p.variants) {
          const variantLabel = v.title !== 'Default Title' ? ` — ${v.title}` : '';
          opts.push({
            value: v.id,
            label: `${p.title}${variantLabel} (${v.price}€)`,
            productTitle: p.title,
          });
        }
      }
      setOptions(opts);
    } catch {
      setOptions([]);
    } finally {
      setLoadingResults(false);
    }
  }, []);

  const handleInputChange = useCallback((val: string) => {
    setInputValue(val);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      searchProducts(val);
    }, 300);
  }, [searchProducts]);

  const handleSelect = useCallback((selected: string[]) => {
    const match = options.find((o) => o.value === selected[0]);
    if (match) {
      onSelect(match.value, match.productTitle, match.label);
      setInputValue('');
    }
  }, [options, onSelect]);

  // Load initial results on focus
  const handleFocus = useCallback(() => {
    if (options.length === 0) searchProducts(inputValue);
  }, [options.length, inputValue, searchProducts]);

  if (value) {
    return (
      <BlockStack gap="200">
        <Text as="p" variant="bodySm" fontWeight="semibold">{label}</Text>
        <InlineStack gap="200" blockAlign="center">
          <Tag onRemove={onClear}>{value.title}</Tag>
        </InlineStack>
      </BlockStack>
    );
  }

  const textField = (
    <Autocomplete.TextField
      onChange={handleInputChange}
      onFocus={handleFocus}
      label={label}
      value={inputValue}
      prefix={<Icon source={SearchIcon} />}
      placeholder="Buscar producto..."
      autoComplete="off"
    />
  );

  return (
    <Autocomplete
      options={options.map((o) => ({ value: o.value, label: o.label }))}
      selected={[]}
      onSelect={handleSelect}
      textField={textField}
      loading={loadingResults}
      emptyState={<Text as="p" variant="bodySm" alignment="center">No se encontraron productos</Text>}
    />
  );
}

// ── Main Component ─────────────────────────────────────────────

export default function Offers() {
  const [offersList, setOffersList] = useState<Offer[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingOffer, setEditingOffer] = useState<Offer | null>(null);

  // Form state
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [active, setActive] = useState(true);
  const [priority, setPriority] = useState('0');
  const [stackable, setStackable] = useState(false);
  const [startsAt, setStartsAt] = useState('');
  const [endsAt, setEndsAt] = useState('');
  const [rules, setRules] = useState<OfferRule[]>([{ ...EMPTY_RULE }]);

  // Translations: { locale: { field: value } }
  const [translations, setTranslations] = useState<Record<string, Record<string, string>>>({});
  const [translationTab, setTranslationTab] = useState(0);
  const [showTranslations, setShowTranslations] = useState(false);

  // Preview
  const [previewAmount, setPreviewAmount] = useState('');
  const [previewResult, setPreviewResult] = useState<any>(null);

  const fetchOffers = useCallback(async () => {
    setLoading(true);
    try {
      const data = await apiGet<{ offers: Offer[] }>('/panel-api/offers');
      setOffersList(data.offers);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchOffers(); }, [fetchOffers]);

  const { selectedResources, allResourcesSelected, handleSelectionChange } =
    useIndexResourceState(offersList.map((o) => ({ id: o.id })));

  function resetForm() {
    setName('');
    setDescription('');
    setActive(true);
    setPriority('0');
    setStackable(false);
    setStartsAt('');
    setEndsAt('');
    setRules([{ ...EMPTY_RULE }]);
    setTranslations({});
    setTranslationTab(0);
    setShowTranslations(false);
  }

  function openCreate() {
    setEditingOffer(null);
    resetForm();
    setShowModal(true);
  }

  async function openEdit(offer: Offer) {
    setEditingOffer(offer);
    setName(offer.name);
    setDescription(offer.description ?? '');
    setActive(offer.active);
    setPriority(String(offer.priority));
    setStackable(offer.stackable);
    setStartsAt(offer.startsAt ? offer.startsAt.slice(0, 16) : '');
    setEndsAt(offer.endsAt ? offer.endsAt.slice(0, 16) : '');
    setRules(offer.rules.length > 0 ? offer.rules : [{ ...EMPTY_RULE }]);
    setTranslations({});
    setTranslationTab(0);
    setShowTranslations(false);
    setShowModal(true);

    // Load existing translations
    try {
      const data = await apiGet<{ translations: Record<string, Record<string, string>> }>(
        `/panel-api/translations/offer/${offer.id}`,
      );
      if (data.translations && Object.keys(data.translations).length > 0) {
        setTranslations(data.translations);
        setShowTranslations(true);
      }
    } catch {
      /* ignore */
    }
  }

  async function handleSave() {
    const payload = {
      name,
      description: description || undefined,
      active,
      priority: parseInt(priority) || 0,
      stackable,
      startsAt: startsAt || null,
      endsAt: endsAt || null,
      rules: rules.map((r, i) => ({
        minOrderAmount: r.minOrderAmount,
        maxOrderAmount: r.maxOrderAmount,
        discountPercent: r.discountPercent,
        discountFixed: r.discountFixed,
        freeShipping: r.freeShipping,
        freeProductVariantId: r.freeProductVariantId,
        freeProductTitle: r.freeProductTitle,
        sortOrder: i,
      })),
    };

    try {
      let offerId = editingOffer?.id;
      if (editingOffer) {
        await apiPut(`/panel-api/offers/${editingOffer.id}`, payload);
      } else {
        const result = await apiPost<{ offer: { id: string } }>('/panel-api/offers', payload);
        offerId = result.offer.id;
      }

      // Save translations
      if (offerId) {
        for (const [locale, fields] of Object.entries(translations)) {
          const hasValues = Object.values(fields).some((v) => v && v.trim() !== '');
          if (hasValues || editingOffer) {
            await apiPut(`/panel-api/translations/offer/${offerId}`, {
              locale,
              translations: fields,
            });
          }
        }
      }

      setShowModal(false);
      fetchOffers();
    } catch (err) {
      console.error('Failed to save offer:', err);
    }
  }

  async function handleDelete(id: string) {
    if (!confirm('¿Eliminar esta oferta?')) return;
    await apiDelete(`/panel-api/offers/${id}`);
    fetchOffers();
  }

  async function handleToggle(offer: Offer) {
    await apiPut(`/panel-api/offers/${offer.id}`, { active: !offer.active });
    fetchOffers();
  }

  async function handlePreview() {
    if (!previewAmount) return;
    const amount = parseFloat(previewAmount);
    if (!amount || amount <= 0) return;
    try {
      const result = await apiPost<any>('/panel-api/offers/preview', { amount });
      setPreviewResult(result);
    } catch { /* ignore */ }
  }

  // Translation helpers
  function updateTranslation(locale: string, field: string, value: string) {
    setTranslations((prev) => ({
      ...prev,
      [locale]: {
        ...(prev[locale] ?? {}),
        [field]: value,
      },
    }));
  }

  // Rule editing helpers
  function updateRule(idx: number, patch: Partial<OfferRule>) {
    setRules((prev) => prev.map((r, i) => i === idx ? { ...r, ...patch } : r));
  }

  function addRule() {
    setRules((prev) => [...prev, { ...EMPTY_RULE, sortOrder: prev.length }]);
  }

  function removeRule(idx: number) {
    setRules((prev) => prev.filter((_, i) => i !== idx));
  }

  // ── Render ─────────────────────────────────────────────────────

  const rowMarkup = offersList.map((offer, idx) => (
    <IndexTable.Row
      id={offer.id}
      key={offer.id}
      position={idx}
      selected={selectedResources.includes(offer.id)}
    >
      <IndexTable.Cell>
        <Button variant="plain" onClick={() => openEdit(offer)}>{offer.name}</Button>
      </IndexTable.Cell>
      <IndexTable.Cell>
        <Badge tone={offer.active ? 'success' : undefined}>
          {offer.active ? 'Activa' : 'Inactiva'}
        </Badge>
      </IndexTable.Cell>
      <IndexTable.Cell>{offer.rules.length} regla(s)</IndexTable.Cell>
      <IndexTable.Cell>
        {offer.rules.map((r, i) => (
          <Text key={i} as="p" variant="bodySm">{describeRule(r)}</Text>
        ))}
      </IndexTable.Cell>
      <IndexTable.Cell>{offer.priority}</IndexTable.Cell>
      <IndexTable.Cell>
        <InlineStack gap="200">
          <Button size="slim" onClick={() => handleToggle(offer)}>
            {offer.active ? 'Desactivar' : 'Activar'}
          </Button>
          <Button size="slim" tone="critical" onClick={() => handleDelete(offer.id)}>
            Eliminar
          </Button>
        </InlineStack>
      </IndexTable.Cell>
    </IndexTable.Row>
  ));

  return (
    <Page
      title="Ofertas"
      primaryAction={{ content: 'Crear oferta', onAction: openCreate }}
    >
      <Layout>
        <Layout.Section>
          <Card>
            <IndexTable
              resourceName={{ singular: 'oferta', plural: 'ofertas' }}
              itemCount={offersList.length}
              headings={[
                { title: 'Nombre' },
                { title: 'Estado' },
                { title: 'Reglas' },
                { title: 'Detalle' },
                { title: 'Prioridad' },
                { title: 'Acciones' },
              ]}
              selectedItemsCount={allResourcesSelected ? 'All' : selectedResources.length}
              onSelectionChange={handleSelectionChange}
              loading={loading}
            >
              {rowMarkup}
            </IndexTable>
          </Card>
        </Layout.Section>

        {/* Preview tool */}
        <Layout.Section variant="oneThird">
          <Card>
            <BlockStack gap="300">
              <Text as="h3" variant="headingSm">Simulador de ofertas</Text>
              <TextField
                label="Importe del pedido (€)"
                type="number"
                value={previewAmount}
                onChange={setPreviewAmount}
                autoComplete="off"
              />
              <Button onClick={handlePreview}>Calcular</Button>
              {previewResult && (
                <BlockStack gap="200">
                  <Divider />
                  {previewResult.appliedOffers?.length > 0 ? (
                    <>
                      {previewResult.appliedOffers.map((ao: any, i: number) => (
                        <Banner key={i} tone="success">
                          <p><strong>{ao.offerName}</strong></p>
                          {ao.discountPercent && <p>{ao.discountPercent}% descuento</p>}
                          {ao.discountFixed && <p>{numToStr(ao.discountFixed)}€ descuento fijo</p>}
                          {ao.freeShipping && <p>✅ Envío gratis</p>}
                          {ao.freeProductTitle && <p>🎁 {ao.freeProductTitle}</p>}
                          <p>Ahorro: {numToStr(ao.discountAmount)}€</p>
                        </Banner>
                      ))}
                      <Text as="p" variant="bodyMd">
                        Total descuento: <strong>{numToStr(previewResult.totalDiscount)}€</strong>
                      </Text>
                      <Text as="p" variant="bodyMd">
                        Importe final: <strong>{numToStr(previewResult.finalAmount)}€</strong>
                      </Text>
                      {previewResult.freeShipping && (
                        <Badge tone="success">Envío gratis</Badge>
                      )}
                    </>
                  ) : (
                    <Banner tone="warning">
                      <p>No hay ofertas aplicables para este importe.</p>
                    </Banner>
                  )}
                </BlockStack>
              )}
            </BlockStack>
          </Card>
        </Layout.Section>
      </Layout>

      {/* Create/Edit Modal */}
      <Modal
        open={showModal}
        onClose={() => setShowModal(false)}
        title={editingOffer ? 'Editar oferta' : 'Nueva oferta'}
        primaryAction={{ content: 'Guardar', onAction: handleSave }}
        secondaryActions={[{ content: 'Cancelar', onAction: () => setShowModal(false) }]}
        size="large"
      >
        <Modal.Section>
          <BlockStack gap="400">
            <TextField label="Nombre" value={name} onChange={setName} autoComplete="off" />
            <TextField label="Descripción" value={description} onChange={setDescription} multiline={2} autoComplete="off" />

            <InlineStack gap="400">
              <Checkbox label="Activa" checked={active} onChange={setActive} />
              <Checkbox label="Acumulable" checked={stackable} onChange={setStackable} />
            </InlineStack>

            <TextField label="Prioridad (mayor = se aplica primero)" type="number" value={priority} onChange={setPriority} autoComplete="off" />

            <InlineStack gap="400">
              <TextField label="Fecha inicio" type="datetime-local" value={startsAt} onChange={setStartsAt} autoComplete="off" />
              <TextField label="Fecha fin" type="datetime-local" value={endsAt} onChange={setEndsAt} autoComplete="off" />
            </InlineStack>

            <Divider />

            <Text as="h3" variant="headingSm">Reglas</Text>
            <Text as="p" variant="bodySm" tone="subdued">
              Cada regla define condiciones (importe mín/máx) y beneficios (descuento, envío gratis, regalo).
              Para ofertas escalonadas, añade varias reglas con distintos rangos de importe.
            </Text>

            {rules.map((rule, idx) => (
              <Card key={idx}>
                <BlockStack gap="300">
                  <InlineStack align="space-between">
                    <Text as="h4" variant="headingSm">Regla {idx + 1}</Text>
                    {rules.length > 1 && (
                      <Button size="slim" tone="critical" onClick={() => removeRule(idx)}>
                        Eliminar
                      </Button>
                    )}
                  </InlineStack>

                  <Text as="p" variant="bodySm" fontWeight="semibold">Condiciones</Text>
                  <InlineStack gap="300">
                    <TextField
                      label="Pedido mínimo (€)"
                      type="number"
                      value={numToStr(rule.minOrderAmount)}
                      onChange={(v) => updateRule(idx, { minOrderAmount: strToNum(v) })}
                      autoComplete="off"
                    />
                    <TextField
                      label="Pedido máximo (€)"
                      type="number"
                      value={numToStr(rule.maxOrderAmount)}
                      onChange={(v) => updateRule(idx, { maxOrderAmount: strToNum(v) })}
                      autoComplete="off"
                    />
                  </InlineStack>

                  <Text as="p" variant="bodySm" fontWeight="semibold">Beneficios</Text>
                  <InlineStack gap="300">
                    <TextField
                      label="Descuento %"
                      type="number"
                      value={rule.discountPercent?.toString() ?? ''}
                      onChange={(v) => updateRule(idx, { discountPercent: v ? parseInt(v) : null })}
                      autoComplete="off"
                    />
                    <TextField
                      label="Descuento fijo (€)"
                      type="number"
                      value={numToStr(rule.discountFixed)}
                      onChange={(v) => updateRule(idx, { discountFixed: strToNum(v) })}
                      autoComplete="off"
                    />
                  </InlineStack>

                  <Checkbox
                    label="Envío gratuito"
                    checked={rule.freeShipping}
                    onChange={(v) => updateRule(idx, { freeShipping: v })}
                  />

                  <ProductSearchField
                    label="Producto regalo"
                    value={rule.freeProductVariantId ? { variantId: rule.freeProductVariantId, title: rule.freeProductTitle ?? rule.freeProductVariantId } : null}
                    onSelect={(variantId, productTitle) => {
                      updateRule(idx, {
                        freeProductVariantId: variantId,
                        freeProductTitle: productTitle,
                      });
                    }}
                    onClear={() => {
                      updateRule(idx, {
                        freeProductVariantId: null,
                        freeProductTitle: null,
                      });
                    }}
                  />
                </BlockStack>
              </Card>
            ))}

            <Button onClick={addRule}>+ Añadir regla</Button>

            <Divider />

            {/* Translations section */}
            <Button
              onClick={() => setShowTranslations(!showTranslations)}
              disclosure={showTranslations ? 'up' : 'down'}
              variant="plain"
            >
              Traducciones ({String(TRANSLATION_LOCALES.length)} idiomas)
            </Button>
            <Collapsible open={showTranslations} id="offer-translations">
              <BlockStack gap="300">
                <Text as="p" variant="bodySm" tone="subdued">
                  Traduce el nombre y la descripción de esta oferta. El idioma por defecto (Español) se configura arriba.
                </Text>
                <Tabs
                  tabs={TRANSLATION_LOCALES.map((l) => ({ id: l.code, content: l.name }))}
                  selected={translationTab}
                  onSelect={setTranslationTab}
                >
                  <Card>
                    <BlockStack gap="300">
                      <Text as="p" variant="bodySm" tone="subdued">
                        Referencia (ES): {name || '(sin nombre)'} — {description || '(sin descripción)'}
                      </Text>
                      <TextField
                        label={`Nombre (${TRANSLATION_LOCALES[translationTab].name})`}
                        value={translations[TRANSLATION_LOCALES[translationTab].code]?.name ?? ''}
                        onChange={(v) => updateTranslation(TRANSLATION_LOCALES[translationTab].code, 'name', v)}
                        autoComplete="off"
                        placeholder={name}
                      />
                      <TextField
                        label={`Descripción (${TRANSLATION_LOCALES[translationTab].name})`}
                        value={translations[TRANSLATION_LOCALES[translationTab].code]?.description ?? ''}
                        onChange={(v) => updateTranslation(TRANSLATION_LOCALES[translationTab].code, 'description', v)}
                        autoComplete="off"
                        multiline={2}
                        placeholder={description}
                      />
                    </BlockStack>
                  </Card>
                </Tabs>
              </BlockStack>
            </Collapsible>
          </BlockStack>
        </Modal.Section>
      </Modal>
    </Page>
  );
}
